// @theme component
import React, { useCallback, useEffect, useState } from 'react';
import { identity, pick, prop } from 'ramda';
import { useDispatch } from 'react-redux';
import { dispatcher } from 'dispatcher';
import { componentService } from '@core/js/services/component.service';
import { usePortletConfig, usePortletSelector, withPortlet } from '@core/js/components/system/Portlet';
import { Button } from '@core/js/components/common/Button';
import { useTranslations } from '@core/js/hooks/i18n';
import { Icon } from '@core/js/components/common/Icon';
import { useFormatter } from '@core/js/hooks/useFormatter';
import { showConfirmationPopupAction } from '@core/js/components/common/popups/ConfirmationPopup';
import { useOnClose } from '@core/js/components/common/PopupManager';
import { useDeviceSelector, useUserSelector } from '@core/js/store/hooks';
import { showCommonPopupAction } from '@core/js/components/common/popups/CommonPopup';
import { ErrorMessage } from '@core/js/components/common/ErrorMessage';
import { useGetPlayerTags, useSetPlayerTags } from '@core/js/hooks/socket';
import { showNevisPasskeysRenamePopupAction } from './actions';
import reducer, { initialState } from './reducer';
import { setFidoAuthenticators } from './server';
import { MONTHS, NEVIS } from './constants';
import { fetchNevisPasskeys, setPlayerEnrollmentCompleted, getPlayerNevisTag } from './helpers';

import style from './index.scss';
import { IFidoAuthentificatorData, INevisData } from './types';

export const NevisPasskeysBare = ({ fetchPortlet }: any) => {
    // @ts-expect-error TODO CODERE: usePortletConfig doesn't return any types
    const { tkPrefix, portletType, nevisTagName, maxPasskeysCount } = usePortletConfig();
    const getPlayerTags = useGetPlayerTags();
    const setPlayerTags = useSetPlayerTags();
    const dispatch = useDispatch();
    const formatter = useFormatter();
    const [isNevisEnabled, setIsNevisEnabled] = useState(false);
    const isMobile = useDeviceSelector(prop('isMobile'));
    const { userName, sessionToken } = useUserSelector(pick(['userName', 'sessionToken']));
    const [isDisabled, setDisabledState] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const data = usePortletSelector(prop('data')) as unknown as INevisData;
    const fidoAuthenticators = data.fidoAuthenticators.slice().reverse();
    const t = useTranslations();
    const isSupportedBrowser = window.fidoAuth?.isSupported();
    const onClose = useOnClose();

    const enrollCallback = useCallback(async (response: any) => {
        if (response.errorCode === window.iapiERR_OK) {
            await fetchPortlet({ type: portletType }, { showLoader: false });
            setPlayerEnrollmentCompleted(setPlayerTags, nevisTagName);
            window.localStorage.setItem('hasPasskeys', 'true');
        } else if (response?.userErrors && response?.userErrors?.UserError?.[0]?.templateId === 'fido2_too_many_authenticators') {
            setErrorMessage(response?.userErrors?.UserError?.[0]?.message);
        } else if (response.errorCode === window.fidoAuth?.ERR_NOT_SUPPORTED) {
            setErrorMessage('playtech.mobile.system.nevis-passkeys.passkeys.error.deviceNotSupported');
        } else if (response.errorCode !== window?.fidoAuth?.ERR_CANCELLED) {
            setErrorMessage('playtech.mobile.system.nevis-passkeys.passkeys.error.generic');
        }
    }, [fetchPortlet, nevisTagName, portletType, setPlayerTags]);

    const createPassKey = useCallback(async () => {
        setErrorMessage('');
        if (typeof data === 'undefined') {
            dispatch(showCommonPopupAction({
                // @ts-expect-error TODO CODERE: Incorrect types for showCommonPopupAction
                className: style.revokePopupContainer,
                title: 'playtech.mobile.system.nevis-passkeys.passkeys.message',
                content: t('playtech.mobile.system.nevis-passkeys.passkeys.error.notWork')
            }));
        } else {
            if (isSupportedBrowser) {
                setDisabledState(true);
                const passkeyName = `${t('playtech.mobile.system.nevis-passkeys.passkeys.passkey-name')} #${fidoAuthenticators.length + 1}`;
                await window.fidoAuth.enroll(enrollCallback, passkeyName, userName, sessionToken)
                    .then(() => {
                        dispatcher.dispatch('passkey:add', { success: true });
                        console.log('Enrollment initiated');
                    })
                    .catch((e: any) => {
                        dispatcher.dispatch('passkey:add', { success: false, errorMessage: e?.errorText, errorCode: e?.errorCode });
                        console.error('Could not initiate enrollment', e);
                    })
                    .finally(() => setDisabledState(false));
            }
        }
    }, [data, dispatch, enrollCallback, isSupportedBrowser, sessionToken, t, userName, fidoAuthenticators.length]);

    const setFidoAuthRequest = useCallback(async (updatedAuth: IFidoAuthentificatorData[]) => {
        try {
            await setFidoAuthenticators(updatedAuth);

            if (!updatedAuth.length) {
                window.localStorage.removeItem('hasPasskeys');
            }

            await fetchPortlet({ type: portletType }, { showLoader: false });
            onClose();
        } catch (error) {
            console.error(error);
        }
    }, [fetchPortlet, onClose, portletType]);

    const revokePassKey = useCallback(async (e: React.MouseEvent<HTMLButtonElement | HTMLLinkElement>, index: number) => {
        const updatedAuth: IFidoAuthentificatorData[] = [];
        fidoAuthenticators.forEach((el: IFidoAuthentificatorData, ind) => {
            if (ind !== index) {
                updatedAuth.push(el);
            }
        });
        dispatch(showConfirmationPopupAction({
            closeAll: false,
            isModal: true,
            // @ts-expect-error TODO CODERE: Incorrect types for showConfirmationPopupAction
            className: style.revokePopupContainer,
            title: 'playtech.mobile.system.nevis-passkeys.passkeys.revoke.passkey',
            content: 'playtech.mobile.system.nevis-passkeys.passkeys.revoke.passkey.content',
            buttons: [
                {
                    text: 'playtech.mobile.system.nevis-passkeys.passkeys.cancel',
                    className: style.button,
                    color: 'negative',
                    onClick: onClose
                },
                {
                    onClick: () => setFidoAuthRequest(updatedAuth),
                    text: 'playtech.mobile.system.nevis-passkeys.passkeys.revoke',
                    className: style.button
                }
            ]
        }));
    }, [dispatch, onClose, setFidoAuthRequest, fidoAuthenticators]);

    const renamePassKey = useCallback(async (e: React.MouseEvent<HTMLButtonElement | HTMLLinkElement>, index: number) => {
        let nameAuth = '';
        fidoAuthenticators.forEach((el, ind) => {
            if (ind === index) {
                nameAuth = el.name;
            }
        });

        dispatch(showNevisPasskeysRenamePopupAction({
            title: 'playtech.mobile.system.nevis-passkeys.passkeys.rename.passkey',
            acceptBtnText: 'playtech.mobile.system.nevis-passkeys.passkeys.save',
            declineBtnText: 'playtech.mobile.system.nevis-passkeys.passkeys.cancel',
            nameAuth,
            index,
            fidoAuthenticators,
            fetchPortlet,
            portletType
        }));
    }, [dispatch, fetchPortlet, portletType, fidoAuthenticators]);

    useEffect(() => {
        getPlayerNevisTag(getPlayerTags, setIsNevisEnabled, nevisTagName);

        if (!isSupportedBrowser) {
            setErrorMessage('playtech.mobile.system.nevis-passkeys.passkeys.error.browserNotSupported');
            setDisabledState(true);
        }
        // eslint-disable-next-line
    }, []);

    if (!isNevisEnabled) {
        return null;
    }

    return (<>
        <div className={style.heroTitle}>
            <h3>{t('playtech.mobile.system.nevis-passkeys.passkeys.title')}</h3>
        </div>
        {isMobile && !fidoAuthenticators?.length &&
            <div className={style.noPassKeyContainer}>
                <div className={style.createPasskeysContainer}>
                    <Icon className={style.passKeyIcon}/>
                    <div className={style.subtitle}>
                        {t(`${tkPrefix}noPasskeys.title`)}
                    </div>
                    <Button className={style.createPasskeyBtn}
                            onClick={createPassKey}
                            disabled={isDisabled}>
                        {t(`${tkPrefix}noPasskeys.addPasskey.btn`)}
                    </Button>
                    <div className={style.listContainer}>
                        <ul>
                            <li className={style.titleList}>
                                {t(`${tkPrefix}noPasskeys.definition.title`)}
                                <span>{t(`${tkPrefix}noPasskeys.definition.explanation`)}</span>
                            </li>
                            <li className={style.titleList}>
                                {t(`${tkPrefix}noPasskeys.saving.title`)}
                                <span>{t(`${tkPrefix}noPasskeys.saving.explanation`)}</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        }
        { isMobile && Boolean(fidoAuthenticators.length) && (
            <div className={style.passKeyExistingBlock}>
                <div className={style.passKeyExistingDescription}>
                    {t(`${tkPrefix}passkeys.subtitle`)}
                </div>
                <Button className={style.createPasskeyBtn} onClick={createPassKey} disabled={isDisabled}>
                    {t(`${tkPrefix}noPasskeys.addPasskey.btn`)}
                </Button>
            </div>
        )}

        {!isMobile &&
            <div className={style.noPassKeyContainer}>
                <div className={style.createPasskeysContainer}>
                    <div className={style.iconWrapper}>
                        <Icon className={style.passKeyIcon}/>
                        <Button className={style.createPasskeyBtn}
                                onClick={createPassKey}
                                disabled={isDisabled}>
                            {t(`${tkPrefix}noPasskeys.addPasskey.btn`)}
                        </Button>
                    </div>
                    <div className={style.listContainer}>
                        <ul className={style.noListStyle}>
                            <li className={style.titleList}>
                                {t(`${tkPrefix}noPasskeys.desktop.definition.title`)}
                                <span>{t(`${tkPrefix}noPasskeys.desktop.definition.explanation`)}</span>
                            </li>
                            <li className={style.titleList}>
                                {t(`${tkPrefix}noPasskeys.desktop.device.title`)}
                                <span>{t(`${tkPrefix}noPasskeys.desktop.device.explanation`)}</span>
                            </li>
                            <li className={style.titleList}>
                                {t(`${tkPrefix}noPasskeys.desktop.saving.title`)}
                                <span>{t(`${tkPrefix}noPasskeys.desktop.saving.explanation`)}</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        }
        {errorMessage && <ErrorMessage className={style.genericError} error={errorMessage}/>}
        {Boolean(fidoAuthenticators?.length) &&
            <div className={style.passKeyContainer}>
                <div className={style.subtitle}>
                    {t(`${tkPrefix}passkeys.desktop.subtitle`)}
                    {isMobile ? '\n' : ' '}
                    {`(${data.fidoAuthenticators.length}/${maxPasskeysCount})`}
                </div>
                {fidoAuthenticators.map((code, index) =>
                    <div key={index} className={style.passkeyItemContainer}>
                        <div className={style.passKeyTextWrapper}>
                            <div className={style.passKeyIconContainer}>
                                <Icon className={style.itemIcon}/>
                            </div>
                            <div className={style.passKeyItemInnerContainer}>
                                <div className={style.passKeyInfo}>
                                    <div className={style.passKeyName}>{code?.name}</div>
                                    <div className={style.passKeyData}>
                                        {t(`${tkPrefix}passkeys.createdOn`)}&nbsp;
                                        {t(MONTHS[Number(formatter.date(code?.enrolledAt, '%m')) - 1])}&nbsp;
                                        {formatter.date(code?.enrolledAt, '%d, %Y')}
                                    </div>
                                </div>
                                <div className={style.passKeyItemButtonsContainer}>
                                    <Button view='primary' color='negative' onClick={e => revokePassKey(e, index)} className={style.revokeBtn}>
                                        {t(`${tkPrefix}passkeys.revoke`)}
                                    </Button>
                                    <Button view='primary' color='success' onClick={e => renamePassKey(e, index)}>
                                        {t(`${tkPrefix}passkeys.rename`)}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>)}
            </div>
        }
    </>);
};
// @ts-expect-error TODO CODERE: withPortlet doesn't work correctly with TS files
export const NevisPasskeys = withPortlet({
    // @ts-expect-error JS file
    forLoggedIn: true,
    fetch: fetchNevisPasskeys,
    onResponse: identity,
    reducer,
    initialState
})(NevisPasskeysBare);

componentService.registerPageComponents({ [NEVIS]: NevisPasskeys });
